import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  Button,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Inventory2 } from '@mui/icons-material';
import scmService, {
  PendingPoItem,
  PurchaseOrderOption,
  WarehouseOption,
  MatchTolerances,
  LandedCostInput,
} from '../../services/scmService';
import {
  P,
  cardSx,
  fieldSx,
  priBtn,
  outBtn,
  gridInput,
  thSx,
  thTitle,
  PX,
  FieldLabel,
  StatusPill,
  WorkspaceHeader,
  TotalsStrip,
  ValidationStrip,
} from '../../components/common/workspaceKit';

/** DECIMAL columns arrive as strings over the wire (see scmService.ts header comment). */
const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const EPSILON = 0.005;
const todayISO = () => new Date().toISOString().slice(0, 10);

interface LineDraft {
  receiving: string;
  unitPrice: string;
  batchNumber: string;
  expiryDate: string;
  manufacturingDate: string;
  supplierBatchRef: string;
}

const emptyLine: LineDraft = { receiving: '', unitPrice: '', batchNumber: '', expiryDate: '', manufacturingDate: '', supplierBatchRef: '' };

interface LandedDraft {
  key: string;
  costType: LandedCostInput['costType'];
  amount: string;
  allocationMethod: NonNullable<LandedCostInput['allocationMethod']>;
}
let landedKeySeq = 0;
const newLanded = (): LandedDraft => ({ key: `lc-${++landedKeySeq}`, costType: 'FREIGHT', amount: '', allocationMethod: 'VALUE' });
const LANDED_TYPES: LandedCostInput['costType'][] = ['FREIGHT', 'CUSTOMS', 'INSURANCE', 'CLEARING', 'OVERHEAD', 'OTHER'];

interface GoodsReceiptFormDialogProps {
  open?: boolean;
  onClose?: () => void;
  onCreated?: () => void;
}

// Rendered as a routed PAGE (so the app sidebar stays visible) — no longer a
// full-screen Dialog. onClose/onCreated default to navigating back to the list.
const GoodsReceiptFormDialog: React.FC<GoodsReceiptFormDialogProps> = ({ open = true, onClose: onCloseProp, onCreated: onCreatedProp }) => {
  const navigate = useNavigate();
  const onClose = onCloseProp || (() => navigate('/procurement/goods-receipts'));
  const onCreated = onCreatedProp || (() => navigate('/procurement/goods-receipts'));
  const [pos, setPos] = useState<PurchaseOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [tolerances, setTolerances] = useState<MatchTolerances | null>(null);
  const [loadingRefData, setLoadingRefData] = useState(false);

  const [selectedPo, setSelectedPo] = useState<PurchaseOrderOption | null>(null);
  const [pendingItems, setPendingItems] = useState<PendingPoItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [lines, setLines] = useState<Record<number, LineDraft>>({});

  const [warehouseId, setWarehouseId] = useState('');
  const [receiptDate, setReceiptDate] = useState(todayISO());
  const [vendorDcNumber, setVendorDcNumber] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [ewayBillNumber, setEwayBillNumber] = useState('');
  const [remarks, setRemarks] = useState('');

  const [landedCosts, setLandedCosts] = useState<LandedDraft[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setSelectedPo(null);
    setPendingItems([]);
    setLines({});
    setWarehouseId('');
    setReceiptDate(todayISO());
    setVendorDcNumber('');
    setVehicleNumber('');
    setEwayBillNumber('');
    setRemarks('');
    setLandedCosts([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingRefData(true);
    Promise.all([
      scmService.listPurchaseOrders(),
      scmService.listWarehouses(),
      scmService.getTolerances(),
    ])
      .then(([poRes, whRes, tolRes]) => {
        setPos(poRes.purchaseOrders.filter((po) => po.status !== 'CANCELLED'));
        setWarehouses(whRes.warehouses.filter((w) => Number(w.is_active) === 1 || w.is_active === true));
        setTolerances(tolRes.tolerances);
      })
      .catch((err: any) => setError(err.message || 'Failed to load reference data'))
      .finally(() => setLoadingRefData(false));
  }, [open, resetForm]);

  const handleSelectPo = (po: PurchaseOrderOption | null) => {
    setSelectedPo(po);
    setPendingItems([]);
    setLines({});
    setError(null);
    if (!po) return;

    setLoadingItems(true);
    scmService
      .getPendingPoItems(po.id)
      .then(({ items }) => {
        setPendingItems(items);
        const draft: Record<number, LineDraft> = {};
        items.forEach((item) => {
          draft[item.po_item_id] = { ...emptyLine };
        });
        setLines(draft);
      })
      .catch((err: any) => setError(err.message || 'Failed to load PO lines'))
      .finally(() => setLoadingItems(false));
  };

  const allowOverReceipt = tolerances ? Number(tolerances.allow_over_receipt) === 1 || tolerances.allow_over_receipt === true : false;

  const lineViolation = useCallback(
    (item: PendingPoItem): { overBy: number } | null => {
      const draft = lines[item.po_item_id];
      const receiving = toNum(draft?.receiving);
      if (receiving <= 0) return null;
      const ordered = toNum(item.ordered_quantity);
      const alreadyReceived = toNum(item.received_quantity);
      const cumulative = alreadyReceived + receiving;
      const overBy = cumulative - ordered;
      return overBy > EPSILON ? { overBy } : null;
    },
    [lines]
  );

  const activeLines = useMemo(
    () => pendingItems.filter((item) => toNum(lines[item.po_item_id]?.receiving) > 0),
    [pendingItems, lines]
  );

  const totalReceivedQty = useMemo(
    () => activeLines.reduce((sum, item) => sum + toNum(lines[item.po_item_id]?.receiving), 0),
    [activeLines, lines]
  );

  const blockingViolation = useMemo(
    () => !allowOverReceipt && activeLines.some((item) => lineViolation(item) !== null),
    [activeLines, allowOverReceipt, lineViolation]
  );

  const canSubmit =
    !!selectedPo && !!warehouseId && !!receiptDate && activeLines.length > 0 && !blockingViolation && !submitting;

  const updateLine = (poItemId: number, patch: Partial<LineDraft>) => {
    setLines((prev) => ({ ...prev, [poItemId]: { ...prev[poItemId], ...patch } }));
  };

  const handleSubmit = async () => {
    if (!selectedPo || !warehouseId) return;
    setSubmitting(true);
    setError(null);
    try {
      const items = activeLines.map((item) => {
        const draft = lines[item.po_item_id];
        return {
          poItemId: item.po_item_id,
          receivedQuantity: toNum(draft.receiving),
          ...(draft.unitPrice.trim() ? { unitPrice: toNum(draft.unitPrice) } : {}),
          ...(draft.batchNumber.trim() ? { batchNumber: draft.batchNumber.trim() } : {}),
          ...(draft.expiryDate ? { expiryDate: draft.expiryDate } : {}),
          ...(draft.manufacturingDate ? { manufacturingDate: draft.manufacturingDate } : {}),
          ...(draft.supplierBatchRef.trim() ? { supplierBatchRef: draft.supplierBatchRef.trim() } : {}),
        };
      });

      const landed: LandedCostInput[] = landedCosts
        .filter((lc) => toNum(lc.amount) > 0)
        .map((lc) => ({ costType: lc.costType, amount: toNum(lc.amount), allocationMethod: lc.allocationMethod }));

      await scmService.createGoodsReceipt({
        poId: selectedPo.id,
        warehouseId: Number(warehouseId),
        receiptDate,
        vendorDcNumber: vendorDcNumber.trim() || null,
        vehicleNumber: vehicleNumber.trim() || null,
        ewayBillNumber: ewayBillNumber.trim() || null,
        remarks: remarks.trim() || null,
        items,
        ...(landed.length ? { landedCosts: landed } : {}),
      });

      onCreated();
      onClose();
    } catch (err: any) {
      // The server's 409 over-receipt message already names the exact
      // quantities involved — surfaced verbatim rather than replaced with a
      // generic toast, since it is the actionable information here.
      setError(err.message || 'Failed to create goods receipt');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
      <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
        <WorkspaceHeader
          onBack={onClose}
          title="New Goods Receipt"
          subtitle="Receive against a purchase order — quantities, batches and match tolerances are checked against the PO."
          chip={<StatusPill label="DRAFT" tone="draft" />}
          actions={
            <>
              <Button variant="outlined" onClick={onClose} disabled={submitting} sx={outBtn}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Inventory2 sx={{ fontSize: 18 }} />}
                onClick={handleSubmit}
                disabled={!canSubmit}
                sx={priBtn}
              >
                {submitting ? 'Creating…' : 'Create Goods Receipt'}
              </Button>
            </>
          }
        />

        <Box sx={{ px: PX, pt: 2.5 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {blockingViolation && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              <AlertTitle>Over-receipt not allowed</AlertTitle>
              One or more lines would receive more than the ordered quantity, and this tenant's match
              tolerances do not allow over-receipt. Reduce the quantity or enable "Allow Over-Receipt" in
              Match Tolerances first — the server will reject this with a 409 otherwise.
            </Alert>
          )}

          {loadingRefData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/* Metadata */}
              <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Receipt Details</Typography>
                </Box>
                <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
                  <Box>
                    <FieldLabel text="Purchase Order" required />
                    <Autocomplete
                      size="small"
                      options={pos}
                      getOptionLabel={(o) => `${o.po_number} — ${o.vendor_name}`}
                      value={selectedPo}
                      onChange={(_, v) => handleSelectPo(v)}
                      renderInput={(params) => <TextField {...params} placeholder="Select PO" sx={fieldSx} />}
                    />
                  </Box>
                  <Box>
                    <FieldLabel text="Warehouse" required />
                    <TextField select fullWidth size="small" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} sx={fieldSx}>
                      {warehouses.map((w) => (
                        <MenuItem key={w.id} value={w.id}>{w.warehouse_name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FieldLabel text="Receipt Date" required />
                    <TextField type="date" fullWidth size="small" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} sx={fieldSx} />
                  </Box>
                  <Box>
                    <FieldLabel text="Vendor DC Number" />
                    <TextField fullWidth size="small" value={vendorDcNumber} onChange={(e) => setVendorDcNumber(e.target.value)} sx={fieldSx} />
                  </Box>
                  <Box>
                    <FieldLabel text="Vehicle Number" />
                    <TextField fullWidth size="small" value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} sx={fieldSx} />
                  </Box>
                  <Box>
                    <FieldLabel text="E-Way Bill Number" />
                    <TextField fullWidth size="small" value={ewayBillNumber} onChange={(e) => setEwayBillNumber(e.target.value)} sx={fieldSx} />
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <FieldLabel text="Remarks" />
                    <TextField fullWidth size="small" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} sx={fieldSx} />
                  </Box>
                </Box>
              </Paper>

              {/* PO lines — full width */}
              {selectedPo && (
                <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>PO Lines</Typography>
                  </Box>

                  {loadingItems ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : pendingItems.length === 0 ? (
                    <Box sx={{ p: { xs: 2, md: 2.75 } }}>
                      <Alert severity="info" sx={{ borderRadius: '10px' }}>This PO has no lines, or every line is already fully received.</Alert>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={thSx}><Typography sx={thTitle}>Product</Typography></TableCell>
                            <TableCell align="right" sx={{ ...thSx, width: 100 }}><Typography sx={thTitle}>Ordered</Typography></TableCell>
                            <TableCell align="right" sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Already Received</Typography></TableCell>
                            <TableCell align="right" sx={{ ...thSx, width: 100 }}><Typography sx={thTitle}>Pending</Typography></TableCell>
                            <TableCell sx={{ ...thSx, width: 140 }}><Typography sx={thTitle}>Receiving Now <Box component="span" sx={{ color: P.red }}>*</Box></Typography></TableCell>
                            <TableCell sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>Unit Price</Typography></TableCell>
                            <TableCell sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Lot / Batch No</Typography></TableCell>
                            <TableCell sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Supplier Lot</Typography></TableCell>
                            <TableCell sx={{ ...thSx, width: 150 }}><Typography sx={thTitle}>Mfg Date</Typography></TableCell>
                            <TableCell sx={{ ...thSx, width: 150 }}><Typography sx={thTitle}>Expiry</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingItems.map((item) => {
                            const draft = lines[item.po_item_id] || emptyLine;
                            const violation = lineViolation(item);
                            const pending = toNum(item.ordered_quantity) - toNum(item.received_quantity);
                            return (
                              <TableRow key={item.po_item_id} sx={{ '& td': { borderBottom: `1px solid ${P.tline}`, py: 0.75 } }}>
                                <TableCell>
                                  <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: P.strong }}>
                                    {item.product_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: '12px', color: P.muted }}>
                                    {item.product_code} · {item.unit_of_measure}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '13.5px', color: P.text }}>
                                  {toNum(item.ordered_quantity)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '13.5px', color: P.text }}>
                                  {toNum(item.received_quantity)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '13.5px', fontWeight: 700, color: P.strong }}>
                                  {Math.max(pending, 0)}
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={draft.receiving}
                                    onChange={(e) => updateLine(item.po_item_id, { receiving: e.target.value })}
                                    error={!!violation && !allowOverReceipt}
                                    helperText={
                                      violation
                                        ? allowOverReceipt
                                          ? `Over-receipt by ${violation.overBy.toFixed(2)} — allowed by tolerance`
                                          : `Exceeds ordered qty by ${violation.overBy.toFixed(2)}`
                                        : undefined
                                    }
                                    inputProps={{ min: 0, step: '0.01' }}
                                    fullWidth
                                    sx={gridInput}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    placeholder={String(toNum(item.unit_price))}
                                    value={draft.unitPrice}
                                    onChange={(e) => updateLine(item.po_item_id, { unitPrice: e.target.value })}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    fullWidth
                                    sx={gridInput}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={draft.batchNumber}
                                    onChange={(e) => updateLine(item.po_item_id, { batchNumber: e.target.value })}
                                    fullWidth
                                    sx={gridInput}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={draft.supplierBatchRef}
                                    onChange={(e) => updateLine(item.po_item_id, { supplierBatchRef: e.target.value })}
                                    fullWidth
                                    sx={gridInput}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="date"
                                    value={draft.manufacturingDate}
                                    onChange={(e) => updateLine(item.po_item_id, { manufacturingDate: e.target.value })}
                                    fullWidth
                                    sx={gridInput}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="date"
                                    value={draft.expiryDate}
                                    onChange={(e) => updateLine(item.po_item_id, { expiryDate: e.target.value })}
                                    fullWidth
                                    sx={gridInput}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              )}

              {/* Landed costs (gap C) */}
              {selectedPo && (
                <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, borderBottom: `1px solid ${P.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Landed Costs (freight / customs / insurance)</Typography>
                      <Typography sx={{ fontSize: '12px', color: P.muted }}>
                        Apportioned across received lines and capitalised into inventory value (Dr Inventory / Cr Landed Cost Clearing).
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small" sx={outBtn} onClick={() => setLandedCosts((prev) => [...prev, newLanded()])}>
                      Add Landed Cost
                    </Button>
                  </Box>
                  {landedCosts.length > 0 && (
                    <Box sx={{ p: { xs: 2, md: 2.75 }, display: 'grid', gap: 1.5 }}>
                      {landedCosts.map((lc) => (
                        <Box key={lc.key} sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr auto' }, alignItems: 'center' }}>
                          <TextField select size="small" label="Cost Type" value={lc.costType} sx={fieldSx}
                            onChange={(e) => setLandedCosts((prev) => prev.map((x) => x.key === lc.key ? { ...x, costType: e.target.value as LandedCostInput['costType'] } : x))}>
                            {LANDED_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                          </TextField>
                          <TextField size="small" type="number" label="Amount" value={lc.amount} inputProps={{ min: 0, step: '0.01' }} sx={fieldSx}
                            onChange={(e) => setLandedCosts((prev) => prev.map((x) => x.key === lc.key ? { ...x, amount: e.target.value } : x))} />
                          <TextField select size="small" label="Allocate By" value={lc.allocationMethod} sx={fieldSx}
                            onChange={(e) => setLandedCosts((prev) => prev.map((x) => x.key === lc.key ? { ...x, allocationMethod: e.target.value as NonNullable<LandedCostInput['allocationMethod']> } : x))}>
                            <MenuItem value="VALUE">Value</MenuItem>
                            <MenuItem value="QTY">Quantity</MenuItem>
                            <MenuItem value="WEIGHT">Weight</MenuItem>
                          </TextField>
                          <Button color="error" size="small" onClick={() => setLandedCosts((prev) => prev.filter((x) => x.key !== lc.key))}>Remove</Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {/* Totals + status strip */}
              <Box sx={{ mb: 1.5 }}>
                <TotalsStrip
                  cells={[
                    { label: 'Lines', value: pendingItems.length },
                    { label: 'Total Received Qty', value: totalReceivedQty },
                    { label: 'Against PO', value: selectedPo?.po_number || '—' },
                    { label: 'Status', value: 'DRAFT' },
                  ]}
                />
              </Box>

              {/* Validation strip */}
              <ValidationStrip
                items={[
                  { ok: !!selectedPo, label: 'PO selected' },
                  { ok: !!warehouseId, label: 'Warehouse selected' },
                  { ok: !!receiptDate, label: 'Receipt date set' },
                  { ok: activeLines.length > 0, label: 'Received quantity entered' },
                ]}
              />
            </>
          )}
        </Box>
      </Box>
  );
};

export default GoodsReceiptFormDialog;
